import type { TutorialStep } from "@brookbush/types";

import { useAuth, useUser } from "@clerk/nextjs";
import { usePlausible } from "next-plausible";
import { useEffect, useRef, useState } from "react";
import Joyride from "react-joyride-react-19";

import { useQueryParam, useUserAction } from "~/hooks";
import useUserFlags from "~/hooks/useUserFlags";

interface TutorialProps {
	steps: TutorialStep[];
	tutorialKey: string;
	trackingEventTitle: string;
}

const Tutorial = ({
	steps,
	tutorialKey,
	trackingEventTitle,
}: TutorialProps) => {
	const { userId } = useAuth();
	const { user } = useUser();
	const { userAction } = useUserAction();
	const { userFlags } = useUserFlags();
	const [tutorialParam, setTutorialParam] = useQueryParam(`tutorial`);

	const track = usePlausible();

	const startTutorial = useRef<(() => void) | null>(null);

	const [isTutorialRequired, setIsTutorialRequired] = useState(
		!!(tutorialParam === "true" && userId),
	);

	const tutorialStatus = (user?.unsafeMetadata as any)?.onboarding?.[
		tutorialKey
	];

	const isTutorialBlocked = !userFlags || userFlags.shouldShowConversionModal;

	useEffect(() => {
		if (
			tutorialStatus !== "finished" &&
			tutorialStatus !== "skipped" &&
			userId &&
			!userAction
		) {
			setIsTutorialRequired(true);
			setTimeout(() => {
				if (isTutorialBlocked) return;

				startTutorial.current?.();

				track(`Tutorial`, {
					props: {
						triggerType: "Onboarding",
						step: "Started",
					},
				});
			}, 500);
		}
	}, [isTutorialBlocked, track, tutorialStatus, userAction, userId]);

	useEffect(() => {
		if (tutorialParam === "true") {
			setIsTutorialRequired(true);

			setTimeout(() => {
				startTutorial.current?.();

				track(`Tutorial`, {
					props: {
						triggerType: "Manual",
						tutorialType: tutorialKey,
						step: "Started",
					},
				});
			}, 500);
		}
	}, [startTutorial, track, trackingEventTitle, tutorialKey, tutorialParam]);

	return (
		<Joyride
			styles={{
				options: {
					primaryColor: "var(--color-primary)",
					backgroundColor: "var(--color-background)",
					arrowColor: "var(--color-background)",
				},
			}}
			steps={steps}
			callback={(data) => {
				if (data.status === "finished") {
					track(`Tutorial`, {
						props: {
							triggerType: tutorialParam === "true" ? "Manual" : "Onboarding",
							tutorialType: tutorialKey,
							step: "Finished",
						},
					});
				}

				if (data.action === "close") {
					track(`Tutorial`, {
						props: {
							triggerType: tutorialParam === "true" ? "Manual" : "Onboarding",
							tutorialType: tutorialKey,
							step: "Skipped",
						},
					});
				}

				if (data.action === "close" || data.status === "finished") {
					user?.update({
						unsafeMetadata: {
							...user?.unsafeMetadata,
							onboarding: {
								...((user?.unsafeMetadata?.onboarding as {
									[key: string]: string;
								}) || {}),
								[tutorialKey]: data.action === "close" ? "skipped" : "finished",
							},
						},
					});

					setTutorialParam(null);
					setIsTutorialRequired(false);
				}
			}}
			continuous
			scrollOffset={200}
			run={isTutorialRequired && !isTutorialBlocked}
			getHelpers={(helpers) => {
				startTutorial.current = helpers.open;
			}}
		/>
	);
};

export default Tutorial;
